import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import { Spin } from "antd";
import axios from "axios";
import config from "../../config";
import PrintHeaderAccount from "../../Pdf/PrintHeaderAccount";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../Pdf/PrintFormateNumber";
import { saleReportStyles } from "../../Pdf/pdfStyles";
import PrintFooter from "../../Pdf/PrintFooter";
import { getDecimalPlaces } from "../Shared/Utility";

const READING_COL = {
  sr: 26,
  pumpNo: 40,
  product: 120,
  lastReading: 85,
  currentReading: 85,
  returned: 66,
  qty: 145,
};

const READING_TABLE_WIDTH =
  READING_COL.sr +
  READING_COL.pumpNo +
  READING_COL.product +
  READING_COL.lastReading +
  READING_COL.currentReading +
  READING_COL.returned +
  READING_COL.qty;

const DETAIL_COL = {
  sr: 26,
  receipt: 46,
  product: 90,
  account: 90,
  vehicle: 56,
  qty: 62,
  price: 62,
  cash: 40,
  amount: 95,
};

const DETAIL_TABLE_WIDTH =
  DETAIL_COL.sr +
  DETAIL_COL.receipt +
  DETAIL_COL.product +
  DETAIL_COL.account +
  DETAIL_COL.vehicle +
  DETAIL_COL.qty +
  DETAIL_COL.price +
  DETAIL_COL.cash +
  DETAIL_COL.amount;

const calcReadingQty = (item) =>
  parseReportAmount(item?.Current_Reading) -
  parseReportAmount(item?.Last_Reading) -
  parseReportAmount(item?.Returned);

const lineAmount = (item) => {
  const total = parseReportAmount(item?.TotalAmount ?? item?.Total);
  if (total !== 0) return total;
  return parseReportAmount(item?.Price) * parseReportAmount(item?.Quantity);
};

function PrintDownloadNewSale() {
  const params = useParams();
  const saleId = params.id;

  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");

  const [loading, setLoading] = useState(true);
  const [listOfReading, setListOfReading] = useState([]);
  const [printData, setPrintData] = useState({});
  const [saleDetail, setSaleDetail] = useState([]);

  const priceDecimals = Math.min(getDecimalPlaces(), 6);

  useEffect(() => {
    document.title = "Print Sale";
    if (!UserID || !AccessKey || !saleId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Sale/GetSaleByID",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        ID: saleId,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setPrintData(response.data);
          setListOfReading(response.data.listofReadings || []);
          setSaleDetail(response.data.listofSale || []);
        } else {
          setPrintData({});
          setListOfReading([]);
          setSaleDetail([]);
        }
      })
      .catch(function () {
        setPrintData({});
        setListOfReading([]);
        setSaleDetail([]);
      })
      .finally(function () {
        setLoading(false);
      });
  }, [UserID, AccessKey, saleId]);

  const activeReadings = useMemo(() => {
    return (listOfReading || []).filter((item) => {
      const qty = calcReadingQty(item);
      return (
        parseReportAmount(item?.Current_Reading) !== 0 ||
        parseReportAmount(item?.Last_Reading) !== 0 ||
        parseReportAmount(item?.Returned) !== 0 ||
        qty !== 0
      );
    });
  }, [listOfReading]);

  const showNozzleSection =
    printData.ShowNozzles !== false && activeReadings.length > 0;

  const fmtMoney = (val) => PrintFormateNumber(parseReportAmount(val), 0);
  const fmtQty = (val) => PrintFormateNumber(parseReportAmount(val));
  const fmtPrice = (val) =>
    PrintFormateNumber(parseReportAmount(val), priceDecimals);

  const invoiceDate =
    printData.Dated && moment(printData.Dated).isValid()
      ? moment(printData.Dated).format(config.date_format)
      : "—";

  const readingRows = () => {
    if (!showNozzleSection) return null;

    return activeReadings.map((item, index) => {
      const qty = calcReadingQty(item);
      return (
        <View key={item.PumpMachineID || index} style={saleReportStyles.row}>
          <View style={[saleReportStyles.td, { width: READING_COL.sr }]}>
            <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
          </View>
          <View style={[saleReportStyles.td, { width: READING_COL.pumpNo }]}>
            <Text style={saleReportStyles.tdValue}>{item.Pump_No ?? "—"}</Text>
          </View>
          <View style={[saleReportStyles.td, { width: READING_COL.product }]}>
            <Text style={saleReportStyles.tdValue}>
              {item.ProductName || "—"}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: READING_COL.lastReading, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {fmtQty(item.Last_Reading)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: READING_COL.currentReading, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {fmtQty(item.Current_Reading)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: READING_COL.returned, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>
              {fmtQty(item.Returned)}
            </Text>
          </View>
          <View
            style={[
              saleReportStyles.td,
              { width: READING_COL.qty, textAlign: "right" },
            ]}
          >
            <Text style={saleReportStyles.tdValue}>{fmtQty(qty)}</Text>
          </View>
        </View>
      );
    });
  };

  const saleRows = () => {
    if (!saleDetail.length) return null;

    return saleDetail.map((item, index) => (
      <View key={item.ID || index} style={saleReportStyles.row}>
        <View style={[saleReportStyles.td, { width: DETAIL_COL.sr }]}>
          <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: DETAIL_COL.receipt }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Receipt_No || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: DETAIL_COL.product }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Product_BE?.Name || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: DETAIL_COL.account }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Account_BE?.Name || "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: DETAIL_COL.vehicle }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Vehicle_No || "—"}
          </Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: DETAIL_COL.qty, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmtQty(item.Quantity)}</Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: DETAIL_COL.price, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmtPrice(item.Price)}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: DETAIL_COL.cash }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Is_Cash ? "Cash" : "Credit"}
          </Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: DETAIL_COL.amount, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmtMoney(lineAmount(item))}</Text>
        </View>
      </View>
    ));
  };

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Sale Invoice</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Invoice No: {printData.InvoiceNo ?? saleId}    Date: {invoiceDate}
            </Text>
            {printData.Description ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Description: {printData.Description}
              </Text>
            ) : null}
          </View>

          {showNozzleSection ? (
            <View style={{ marginBottom: 8 }}>
              <Text
                style={[saleReportStyles.pagetitle, { fontSize: "9pt", marginBottom: 4 }]}
              >
                Nozzle Readings
              </Text>
              <View
                style={[saleReportStyles.table, { width: READING_TABLE_WIDTH }]}
              >
                <View style={saleReportStyles.tableHead}>
                  <View style={[saleReportStyles.th, { width: READING_COL.sr }]}>
                    <Text style={saleReportStyles.thValue}>Sr.#</Text>
                  </View>
                  <View
                    style={[saleReportStyles.th, { width: READING_COL.pumpNo }]}
                  >
                    <Text style={saleReportStyles.thValue}>Pump</Text>
                  </View>
                  <View
                    style={[saleReportStyles.th, { width: READING_COL.product }]}
                  >
                    <Text style={saleReportStyles.thValue}>Product</Text>
                  </View>
                  <View
                    style={[
                      saleReportStyles.th,
                      { width: READING_COL.lastReading, textAlign: "right" },
                    ]}
                  >
                    <Text style={saleReportStyles.thValue}>Last Reading</Text>
                  </View>
                  <View
                    style={[
                      saleReportStyles.th,
                      { width: READING_COL.currentReading, textAlign: "right" },
                    ]}
                  >
                    <Text style={saleReportStyles.thValue}>Current Reading</Text>
                  </View>
                  <View
                    style={[
                      saleReportStyles.th,
                      { width: READING_COL.returned, textAlign: "right" },
                    ]}
                  >
                    <Text style={saleReportStyles.thValue}>Return</Text>
                  </View>
                  <View
                    style={[
                      saleReportStyles.th,
                      { width: READING_COL.qty, textAlign: "right" },
                    ]}
                  >
                    <Text style={saleReportStyles.thValue}>Quantity</Text>
                  </View>
                </View>
                {readingRows()}
              </View>
            </View>
          ) : null}

          <View
            style={[saleReportStyles.table, { width: DETAIL_TABLE_WIDTH }]}
          >
            <View style={saleReportStyles.tableHead}>
              <View style={[saleReportStyles.th, { width: DETAIL_COL.sr }]}>
                <Text style={saleReportStyles.thValue}>Sr.#</Text>
              </View>
              <View style={[saleReportStyles.th, { width: DETAIL_COL.receipt }]}>
                <Text style={saleReportStyles.thValue}>Receipt</Text>
              </View>
              <View style={[saleReportStyles.th, { width: DETAIL_COL.product }]}>
                <Text style={saleReportStyles.thValue}>Product</Text>
              </View>
              <View style={[saleReportStyles.th, { width: DETAIL_COL.account }]}>
                <Text style={saleReportStyles.thValue}>Account</Text>
              </View>
              <View style={[saleReportStyles.th, { width: DETAIL_COL.vehicle }]}>
                <Text style={saleReportStyles.thValue}>Vehicle</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: DETAIL_COL.qty, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Qty</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: DETAIL_COL.price, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Price</Text>
              </View>
              <View style={[saleReportStyles.th, { width: DETAIL_COL.cash }]}>
                <Text style={saleReportStyles.thValue}>Type</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: DETAIL_COL.amount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
            </View>

            {saleRows()}

            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: DETAIL_TABLE_WIDTH - DETAIL_COL.amount,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Grand Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: DETAIL_COL.amount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmtMoney(
                    parseReportAmount(printData.Cash_Total) +
                      parseReportAmount(printData.Credit_Total)
                  )}
                </Text>
              </View>
            </View>
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: DETAIL_TABLE_WIDTH - DETAIL_COL.amount,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Cash Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: DETAIL_COL.amount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmtMoney(printData.Cash_Total)}
                </Text>
              </View>
            </View>
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: DETAIL_TABLE_WIDTH - DETAIL_COL.amount,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Credit Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: DETAIL_COL.amount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmtMoney(printData.Credit_Total)}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
}

export default PrintDownloadNewSale;
